<template>
  <div
    class="post-cards-widget"
    :class="[`style-${widget.cardStyle || 'default'}`, widgetClasses]"
    :style="widgetStyles"
  >
    <!-- Widget Title -->
    <div v-if="widget.title" class="widget-header mb-4">
      <h3 class="widget-title">{{ widget.title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Φόρτωση...</span>
      </div>
      <p class="mt-2 text-muted">Φόρτωση άρθρων...</p>
    </div>

    <!-- Posts Grid - Project Cards Style -->
    <div v-else-if="posts.length > 0 && widget.cardStyle === 'project-cards'" class="posts-grid">
      <div v-for="(post, index) in posts" :key="post.id" :class="['post-card', getCardColorClass(index)]">
        <!-- Card Header -->
        <div class="post-card-header">
          <div class="post-date">{{ formatDate(post.created_at) }}</div>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="post-menu-icon">
            <path fill-rule="evenodd" d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0" clip-rule="evenodd" />
          </svg>
        </div>

        <!-- Card Body -->
        <div class="post-card-body">
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-category">{{ post.category_name || 'Γενικά' }}</p>
          <div class="post-progress">
            <span class="post-progress-label">Δημοφιλία</span>
            <div class="post-progress-bar" :style="{ '--progress-width': getProgressWidth(post.views) }"></div>
            <span class="post-progress-percent">{{ getProgressPercentage(post.views) }}%</span>
          </div>
        </div>

        <!-- Card Footer -->
        <div class="post-card-footer">
          <ul class="post-avatars">
            <li class="post-avatar">
              <img src="https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Author">
            </li>
            <li class="post-avatar">
              <img src="https://images.pexels.com/photos/1043474/pexels-photo-1043474.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" alt="Contributor">
            </li>
            <button class="post-add-btn">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style="width: 1rem; height: 1rem;">
                <path fill-rule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clip-rule="evenodd" />
              </svg>
            </button>
          </ul>

          <router-link :to="`/blog/${post.slug}`" class="post-read-more">
            Περισσότερα
          </router-link>
        </div>
      </div>
    </div>

    <!-- Posts Grid - Default Style -->
    <div v-else-if="posts.length > 0" class="posts-grid">
      <div class="row g-4" :class="`row-cols-2 row-cols-md-${widget.columns || 3}`">
        <div v-for="post in posts" :key="post.id" class="col">
          <BlogPostCard
            :post="post"
            view-mode="grid"
            :is-in-wishlist="isInWishlist(post.id)"
            :show-excerpt="widget.showExcerpt !== false"
            @toggle-wishlist="addToWishlist(post)"
            @quick-view="openQuickView(post)"
          />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state text-center py-4">
      <i class="bi bi-file-earmark-text display-4 text-muted mb-3"></i>
      <h5 class="text-muted">Δεν βρέθηκαν άρθρα</h5>
      <p class="text-muted mb-0">
        {{ widget.categoryId ? 'Δεν υπάρχουν άρθρα σε αυτή την κατηγορία.' : 'Δεν υπάρχουν δημοσιευμένα άρθρα.' }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { PostCardsWidget } from '@/types/pagebuilder'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'
import { UPLOADS_BASE_URL } from '@/config/env'
import { settingsService } from '@/services/api'
import BlogPostCard from '@/components/BlogPostCard.vue'
import { useWishlistStore } from '@/stores/wishlist'
import { useWidgetStyles } from '@/composables/useWidgetStyles'

interface Props {
  widget: PostCardsWidget
}

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string
  content: string
  featured_image: string
  category_id: number
  category_name: string
  created_at: string
  views: number
}

const props = defineProps<Props>()
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

// State
const posts = ref<Post[]>([])
const loading = ref(false)
const navSettings = ref<any>({})

// Computed tooltip colors
const tooltipBgColor = computed(() => navSettings.value.navigation?.backgroundColor || '#fa709a')
const tooltipTextColor = computed(() => navSettings.value.navigation?.textColor || '#ffffff')

// Load navigation settings
const loadNavSettings = async () => {
  try {
    const response = await settingsService.getNavigationSettings()
    if (response.success && response.data) {
      navSettings.value = response.data
    }
  } catch (error) {
    console.error('Error loading navigation settings:', error)
  }
}
const wishlistStore = useWishlistStore()

// Methods
const loadPosts = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({
      limit: props.widget.limit.toString(),
      status: 'published'
    })

    if (props.widget.categoryId) {
      params.append('category_id', props.widget.categoryId.toString())
    }

    // Add featured filter if postType is 'featured'
    if (props.widget.postType === 'featured') {
      params.append('featured', '1')
    }

    const response = await authFetch(`${API_BASE_URL}/posts.php?${params}`)
    const data = await response.json()

    if (data.success && Array.isArray(data.data)) {
      posts.value = data.data
    } else {
      posts.value = []
    }
  } catch (error) {
    posts.value = []
  } finally {
    loading.value = false
  }
}

const getPostImageUrl = (image: string | null) => {
  if (!image) return `${API_BASE_URL}/placeholder.php?width=400&height=250&text=No Image&bg=e9ecef&color=6c757d`
  if (image.startsWith('http')) return image
  return `${UPLOADS_BASE_URL}/posts/${image}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.src = `${API_BASE_URL}/placeholder.php?width=400&height=250&text=Image Error&bg=f8d7da&color=721c24`
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('el-GR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Project cards helper methods
const getCardColorClass = (index: number) => {
  const colors = ['green', 'blue', 'orange', 'red']
  return colors[index % colors.length]
}

const getProgressWidth = (views: number) => {
  // Calculate progress based on views, max 100%
  const maxViews = Math.max(...posts.value.map(p => p.views), 1)
  const percentage = Math.min((views / maxViews) * 100, 100)
  return `${percentage}%`
}

const getProgressPercentage = (views: number) => {
  // Calculate progress based on views, max 100%
  const maxViews = Math.max(...posts.value.map(p => p.views), 1)
  const percentage = Math.min(Math.round((views / maxViews) * 100), 100)
  return percentage
}

// Wishlist functionality
const isInWishlist = (postId: number) => {
  return wishlistStore.isPostInWishlist(postId)
}

const addToWishlist = (post: Post) => {
  if (wishlistStore.isPostInWishlist(post.id)) {
    // Remove from wishlist
    wishlistStore.removePostFromWishlist(post.id)
  } else {
    // Add to wishlist
    wishlistStore.addPostToWishlist(post)
  }
}

// Quick view functionality
const openQuickView = (post: Post) => {
  // Create quick view modal content
  const modal = document.createElement('div')
  modal.className = 'post-quick-view-modal'
  modal.innerHTML = `
    <div class="post-quick-view-overlay" onclick="closeQuickView()">
      <div class="post-quick-view-content" onclick="event.stopPropagation()">
        <button class="post-quick-view-close" onclick="closeQuickView()">
          <i class="bi bi-x-lg"></i>
        </button>
        <div class="post-quick-view-body">
          ${post.featured_image ? `
            <div class="post-quick-view-image">
              <img src="${getPostImageUrl(post.featured_image)}" alt="${post.title}" />
            </div>
          ` : ''}
          <div class="post-quick-view-info">
            <div class="post-category-badge mb-2">
              ${post.category_name || 'Γενικά'}
            </div>
            <h3 class="post-quick-view-title">${post.title}</h3>
            <p class="post-quick-view-excerpt">${post.excerpt || 'Δεν υπάρχει περίληψη διαθέσιμη.'}</p>
            <div class="post-quick-view-actions">
              <a href="/blog/${post.slug}" class="btn btn-primary">
                <i class="bi bi-arrow-right me-2"></i>
                Περισσότερα
              </a>
              <button onclick="addToWishlistFromModal(${post.id})" class="btn btn-outline-secondary">
                <i class="bi bi-heart me-2"></i>
                ${isInWishlist(post.id) ? 'Αφαίρεση από Wishlist' : 'Προσθήκη στη Wishlist'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
  
  document.body.appendChild(modal)
  
  // Add global functions for modal
  ;(window as any).closeQuickView = () => {
    document.body.removeChild(modal)
  }
  
  ;(window as any).addToWishlistFromModal = (postId: number) => {
    const post = posts.value.find(p => p.id === postId)
    if (post) {
      addToWishlist(post)
      // Update button text in modal
      const button = modal.querySelector('.btn-outline-secondary')
      if (button) {
        button.innerHTML = `
          <i class="bi bi-heart me-2"></i>
          ${isInWishlist(postId) ? 'Αφαίρεση από Wishlist' : 'Προσθήκη στη Wishlist'}
        `
      }
    }
  }
  
  // Close on escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      ;(window as any).closeQuickView()
      document.removeEventListener('keydown', handleEscape)
    }
  }
  document.addEventListener('keydown', handleEscape)
}

// Lifecycle
onMounted(() => {
  loadNavSettings()
  loadPosts()
})
</script>

<style scoped>
.post-cards-widget {
  width: 100%;
}

.widget-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #2c3e50;
}

.post-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.post-card-link:hover {
  text-decoration: none;
  color: inherit;
}

.post-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible;
  position: relative;
}

.post-card:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
  z-index: 10;
}

.post-card:hover .post-title {
  white-space: normal;
  overflow: visible;
  background: v-bind(tooltipBgColor);
  color: white !important;
  padding: 0.5rem;
  margin: -0.5rem -0.5rem 0.75rem -0.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  animation: titlePop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes titlePop {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Image Container */
.post-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio - Square */
  overflow: hidden;
  background: #f8f9fa;
}

.post-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
  display: block;
}

.post-card:hover .post-image img {
  transform: scale(1.05);
}

.post-category-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(13, 110, 253, 0.9);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

/* Content */
.post-content {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}

.post-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  color: #2c3e50;
  transition: color 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
}

.post-title::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%) scale(0.8);
  background: v-bind(tooltipBgColor);
  color: v-bind(tooltipTextColor);
  padding: 12px 20px;
  border-radius: 15px;
  font-size: 0.95rem;
  white-space: normal;
  max-width: 280px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
              0 0 0 1px rgba(255, 255, 255, 0.3) inset;
  opacity: 0;
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1000;
  line-height: 1.4;
  text-align: center;
  font-weight: 500;
  backdrop-filter: blur(10px);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.post-title::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid v-bind(tooltipBgColor);
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 999;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.post-card:hover .post-title::after {
  opacity: 1;
  transform: translateX(-50%) scale(1);
  animation: tooltipBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.post-card:hover .post-title::before {
  opacity: 1;
}

@keyframes tooltipBounce {
  0% {
    opacity: 0;
    transform: translateX(-50%) scale(0.8) translateY(10px);
  }
  50% {
    transform: translateX(-50%) scale(1.05) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) scale(1) translateY(0);
  }
}

.post-card-link:hover .post-title {
  color: white !important;
}

.post-meta {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.post-excerpt {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-clamp: 3;
  overflow: hidden;
}

.post-actions {
  margin-top: auto;
}

/* Card Styles */
.style-minimal .post-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.style-minimal .post-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.style-overlay .post-image-wrapper {
  position: relative;
}

.style-overlay .post-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
}

.style-overlay .post-title {
  color: white;
}

.style-overlay .post-meta {
  color: rgba(255, 255, 255, 0.8);
}

.style-overlay .post-excerpt {
  color: rgba(255, 255, 255, 0.9);
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3rem 1rem;
}

.loading-state .spinner-border {
  color: #0d6efd;
}

/* Responsive */
@media (max-width: 768px) {
  .posts-grid {
    padding: 0 7.5%; /* 85% πλάτος - 15% margins */
    margin: 0 auto;
  }
  
  .posts-grid .row {
    --bs-gutter-x: 1rem;
  }
  
  .post-content {
    padding: 1rem;
  }
  
  .widget-title {
    font-size: 1.2rem;
  }
  
  /* Keep square aspect ratio on tablet */
  
  /* Ensure 2 columns on mobile */
  .posts-grid .col {
    flex: 0 0 auto;
    width: 50% !important;
    max-width: 50%;
  }
}

@media (max-width: 576px) {
  /* Maintain 2 columns for mobile */
  .posts-grid .row {
    --bs-gutter-x: 0.75rem;
  }
  
  .posts-grid .col {
    flex: 0 0 auto;
    width: 50% !important;
    max-width: 50%;
  }
  
  .post-content {
    padding: 0.875rem;
  }
  
  /* Keep square aspect ratio on mobile */
  
  .post-title {
    font-size: 0.95rem;
  }
  
  .post-excerpt {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
  
  /* Μικρότερο κουμπί διαβάστε περισσότερα */
  .btn-sm {
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
    border-radius: 0.25rem !important;
  }
}

@media (max-width: 480px) {
  /* Enhanced mobile styles for very small screens */
  .posts-grid {
    padding: 0 5%; /* 90% πλάτος για πολύ μικρές οθόνες */
  }
  
  .posts-grid .row {
    --bs-gutter-x: 0.5rem;
  }
  
  .post-content {
    padding: 0.75rem;
  }
  
  .post-title {
    font-size: 0.9rem;
  }
  
  /* Keep square aspect ratio on very small screens */
  
  /* Ακόμα μικρότερο κουμπί για πολύ μικρές οθόνες */
  .btn-sm {
    font-size: 0.7rem !important;
    padding: 0.2rem 0.4rem !important;
  }
}

/* ============================================
   ENHANCED HOVER EFFECTS & ACTION BUTTONS
   ============================================ */

/* Improved Card Hover Effects */
.post-card {
  position: relative;
  border: 1px solid rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform-style: preserve-3d;
}

.post-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 
    0 20px 40px rgba(0,0,0,0.15),
    0 0 0 1px rgba(255,255,255,0.9);
  border-color: rgba(13, 110, 253, 0.2);
}

.post-card:hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(13, 110, 253, 0.05), rgba(220, 53, 69, 0.05));
  border-radius: 12px;
  z-index: 1;
  pointer-events: none;
}

.post-card .post-content {
  position: relative;
  z-index: 2;
}

/* Action Buttons Overlay */
.post-actions-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  transform: translateX(20px);
  transition: all 0.3s ease;
}

.post-card:hover .post-actions-overlay {
  opacity: 1;
  transform: translateX(0);
}

.action-btn {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  transition: left 0.6s ease;
}

.action-btn:hover::before {
  left: 100%;
}

.wishlist-btn {
  background: rgba(220, 53, 69, 0.9);
  color: white;
}

.wishlist-btn:hover {
  background: #dc3545;
  transform: scale(1.1) rotate(5deg);
}

.wishlist-btn.active {
  background: #dc3545;
  animation: pulse-heart 0.6s ease;
}

@keyframes pulse-heart {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

.quick-view-btn {
  background: rgba(13, 110, 253, 0.9);
  color: white;
}

.quick-view-btn:hover {
  background: #0d6efd;
  transform: scale(1.1) rotate(-5deg);
}

/* Bottom Action Buttons (for cards without images) */
.post-actions-bottom {
  display: flex;
  gap: 8px;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0,0,0,0.08);
}

.action-btn.small {
  width: auto;
  height: 36px;
  padding: 0 12px;
  border-radius: 18px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  font-weight: 500;
}

.action-btn.small:hover {
  transform: translateY(-2px);
}

.read-more-btn {
  background: rgba(40, 167, 69, 0.9);
  color: white;
}

.read-more-btn:hover {
  background: #28a745;
  color: white;
  text-decoration: none;
}

/* Post Title Link */
.post-title-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.3s ease;
}

.post-title-link:hover {
  color: #0d6efd;
  text-decoration: none;
}

/* Quick View Modal Styles */
:global(.post-quick-view-modal) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

:global(.post-quick-view-overlay) {
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

:global(.post-quick-view-content) {
  background: white;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

:global(.post-quick-view-close) {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0,0,0,0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

:global(.post-quick-view-close:hover) {
  background: rgba(220, 53, 69, 0.9);
  color: white;
  transform: scale(1.1);
}

:global(.post-quick-view-body) {
  display: flex;
  max-height: 80vh;
  overflow: hidden;
}

:global(.post-quick-view-image) {
  flex: 0 0 300px;
  height: 400px;
}

:global(.post-quick-view-image img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:global(.post-quick-view-info) {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

:global(.post-quick-view-title) {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2c3e50;
}

:global(.post-quick-view-excerpt) {
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 2rem;
}

:global(.post-quick-view-actions) {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Mobile Responsive for Quick View */
@media (max-width: 768px) {
  :global(.post-quick-view-overlay) {
    padding: 1rem;
  }
  
  :global(.post-quick-view-body) {
    flex-direction: column;
  }
  
  :global(.post-quick-view-image) {
    flex: none;
    height: 200px;
  }
  
  :global(.post-quick-view-info) {
    padding: 1.5rem;
  }
  
  :global(.post-quick-view-actions) {
    flex-direction: column;
  }
}

</style>